// v1.7.0 · CRM · Ficha del contacto: datos, etiquetas, campos personalizados, notas, tareas, oportunidades y actividad
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Trash2, Mail, Phone, Building2, MessageSquare } from 'lucide-react';
import { api, errMsg, money, fdatetime, StatusBadge, Tag, inputCls, btn, Field, CustomInput } from './ui';
import { TaskForm, TaskRow } from './Tasks';
import { OppForm } from './Opportunities';
import { AppointmentForm, ApptBadge, apptWhen } from './Calendar';   // v1.9.0
import { DocModal, DocBadge, money as money2 } from './Payments';
import { LeadBadge } from './Assistant';   // v1.14.0   // v1.10.0 · v1.12.0 · montos con 2 decimales

const BASE_FIELDS = [['first_name', 'Nombre'], ['last_name', 'Apellido'], ['email', 'Correo'], ['phone', 'Teléfono'],
  ['company', 'Empresa'], ['source', 'Fuente'], ['city', 'Ciudad'], ['country', 'País']];
const ACT_LABEL = { created: 'Creación', updated: 'Edición', tag: 'Etiquetas', note: 'Nota', task: 'Tarea', opportunity: 'Oportunidad', stage: 'Etapa', import: 'Importación', message: 'Mensaje', appointment: 'Cita', quote: 'Cotización', invoice: 'Factura', payment: 'Pago', automation: 'Automatización', ai: 'Asistente' };   // v1.14.0 · ai   // v1.8.0 · v1.9.0 · v1.10.0 · v1.11.0

export default function ContactDetail({ meta, contactId }) {
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState('notes');
  const [note, setNote] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [modal, setModal] = useState(null);
  const [denied, setDenied] = useState('');   // v1.12.1 · sin acceso (403): mensaje en la página en vez de «Cargando…» infinito
  const users = useMemo(() => Object.fromEntries(meta.users.map((u) => [u.id, u.name])), [meta.users]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/contacts/${contactId}`);
      setDenied('');
      setC(r);
      setForm({ ...Object.fromEntries(BASE_FIELDS.map(([k]) => [k, r[k] || ''])), owner_id: r.owner_id || '', dnd: !!r.dnd, custom: r.custom || {} });
    } catch (e) {
      if (e?.response?.status === 403) { setDenied(errMsg(e)); return; }
      toast.error(errMsg(e));
      if (e?.response?.status === 404) navigate('/crm?tab=contacts');
    }
  }, [contactId, navigate]);
  useEffect(() => { load(); }, [load]);

  // v1.9.0 · citas del contacto y calendarios para agendar
  const [appts, setAppts] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const loadAppts = useCallback(async () => {
    try {
      const [a, k] = await Promise.all([api.get('/appointments', { contact_id: contactId }), api.get('/calendars')]);
      setAppts(a.items); setCalendars(k.items.filter((x) => x.active !== false));
    } catch { setAppts([]); }
  }, [contactId]);
  useEffect(() => { loadAppts(); }, [loadAppts]);

  // v1.10.0 · cotizaciones y facturas del contacto
  const [docs, setDocs] = useState({ quotes: [], invoices: [] });
  const loadDocs = useCallback(async () => {
    try {
      const [qs, inv] = await Promise.all([api.get('/quotes', { contact_id: contactId }), api.get('/invoices', { contact_id: contactId })]);
      setDocs({ quotes: qs.items, invoices: inv.items });
    } catch { setDocs({ quotes: [], invoices: [] }); }
  }, [contactId]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  if (denied) {
    return (
      <div className="mx-auto max-w-md rounded-xl border border-gray-200 bg-white px-6 py-10 text-center" data-testid="crm-contact-denied">
        <p className="text-[15px] font-bold text-[#0F2A3A]">No puede ver este contacto</p>
        <p className="mt-1 text-sm text-gray-600">{denied}</p>
        <button className={`${btn.primary} mt-4`} onClick={() => navigate('/crm?tab=contacts')}>Volver a Contactos</button>
      </div>
    );
  }
  if (!c || !form) return <div className="py-16 text-center text-sm text-gray-500">Cargando contacto…</div>;

  const save = async (extra = {}) => {
    try { await api.put(`/contacts/${c.id}`, { ...form, owner_id: form.owner_id || null, ...extra }); toast.success('Contacto guardado'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setTags = (tags) => save({ tags });
  const addTag = () => { const t = tagInput.trim().toLowerCase(); if (t && !c.tags.includes(t)) setTags([...c.tags, t]); setTagInput(''); };
  const addNote = async () => {
    try { await api.post(`/contacts/${c.id}/notes`, { body: note }); setNote(''); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const delNote = async (n) => { try { await api.del(`/notes/${n.id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const toggleTask = async (t) => { try { await api.put(`/tasks/${t.id}`, { status: t.status === 'completed' ? 'pending' : 'completed' }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const delTask = async (t) => { if (!window.confirm('¿Eliminar la tarea?')) return; try { await api.del(`/tasks/${t.id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async () => {
    if (!window.confirm(`¿Eliminar a ${c.name}? Se borran sus notas, tareas y actividad; sus oportunidades se conservan sin contacto.`)) return;
    try { await api.del(`/contacts/${c.id}`); toast.success('Contacto eliminado'); navigate('/crm?tab=contacts'); } catch (e) { toast.error(errMsg(e)); }
  };
  // v1.8.0 · abre (o crea) la conversación del contacto en la bandeja
  const openConversation = async () => {
    try { const cv = await api.post('/conversations', { contact_id: c.id }); navigate(`/crm?tab=conversations&c=${cv.id}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const pipeOf = (o) => meta.pipelines.find((p) => p.id === o.pipeline_id);
  const tabCls = (on) => `border-b-2 px-3 py-2 text-sm font-semibold ${on ? 'border-[#2F3D8E] text-[#2F3D8E]' : 'border-transparent text-gray-500 hover:text-gray-800'}`;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[360px_minmax(0,1fr)]" data-testid="crm-contact-detail">
      <aside className="min-w-0 space-y-4">
        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#2F3D8E] text-sm font-bold text-white">
              {c.name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-[17px] font-extrabold text-[#0F2A3A]">{c.name}</h2>
              <div className="text-xs text-gray-500">Creado {fdatetime(c.created_at)}{c.created_by ? ` por ${c.created_by}` : ''}</div>
            </div>
          </div>
          <div className="mt-3 space-y-1 text-sm text-gray-700">
            {c.email && <a className="flex items-center gap-2 hover:text-[#2F3D8E]" href={`mailto:${c.email}`}><Mail size={14} />{c.email}</a>}
            {c.phone && <a className="flex items-center gap-2 hover:text-[#2F3D8E]" href={`tel:${c.phone}`}><Phone size={14} />{c.phone}</a>}
            {c.company && <div className="flex items-center gap-2"><Building2 size={14} />{c.company}</div>}
          </div>
          {c.ai_level && (   // v1.14.0 · evaluación del asistente
            <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50/60 p-2.5 text-xs text-gray-700" data-testid="ai-evaluation">
              <div className="flex flex-wrap items-center gap-2"><b className="text-violet-900">Evaluación del asistente</b><LeadBadge level={c.ai_level} score={c.ai_score} />
                {c.ai_service && <span className="text-gray-500">· {c.ai_service}</span>}</div>
              {c.ai_reason && <p className="mt-1">{c.ai_reason}</p>}
              {c.ai_next_action && <p className="mt-1"><b>Siguiente paso:</b> {c.ai_next_action}</p>}
              <p className="mt-1 text-[11px] text-gray-500">{fdatetime(c.ai_scored_at)}</p>
            </div>
          )}
          <div className="mt-3">
            <div className="flex flex-wrap gap-1">{c.tags.map((t) => <Tag key={t} onRemove={() => setTags(c.tags.filter((x) => x !== t))}>{t}</Tag>)}</div>
            <div className="mt-2 flex gap-2">
              <input className={inputCls} value={tagInput} onChange={(e) => setTagInput(e.target.value)} list="crm-tags-cd" placeholder="Agregar etiqueta"
                     onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} />
              <datalist id="crm-tags-cd">{meta.tags.map((t) => <option key={t} value={t} />)}</datalist>
              <button className={btn.ghost} onClick={addTag}>+</button>
            </div>
          </div>
        </section>

        <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="text-[14px] font-bold text-[#0F2A3A]">Datos del contacto</h3>
          {BASE_FIELDS.map(([k, l]) => (
            <Field key={k} label={l}><input className={inputCls} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></Field>
          ))}
          <Field label="Responsable">
            <select className={inputCls} value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })}>
              <option value="">Sin asignar</option>{meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.dnd} onChange={(e) => setForm({ ...form, dnd: e.target.checked })} /> No contactar</label>
          {meta.fields.length > 0 && (
            <div className="space-y-3 border-t pt-3">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-500">Campos personalizados</div>
              {meta.fields.map((f) => (
                <Field key={f.id} label={`${f.name}${f.required ? ' *' : ''}`} group={f.type === 'multiselect'}>
                  <CustomInput field={f} value={form.custom[f.id]} onChange={(v) => setForm({ ...form, custom: { ...form.custom, [f.id]: v } })} />
                </Field>
              ))}
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button className={btn.primary} onClick={() => save()}>Guardar cambios</button>
            {meta.is_admin && <button className={btn.danger} onClick={remove}><Trash2 size={14} /> Eliminar</button>}
          </div>
        </section>
      </aside>

      <div className="min-w-0 space-y-4">
        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-[14px] font-bold text-[#0F2A3A]">Oportunidades</h3>
            <div className="flex gap-2">
              <button className={btn.ghost} onClick={openConversation} data-testid="contact-message"><MessageSquare size={14} /> Mensaje</button>
              <button className={btn.ghost} onClick={() => setModal({ t: 'opp', opp: null, pipeline: meta.pipelines[0] })}><Plus size={14} /> Oportunidad</button>
            </div>
          </div>
          {c.opportunities.length === 0 ? <p className="text-sm text-gray-500">Sin oportunidades.</p> : (
            <ul className="divide-y text-sm">
              {c.opportunities.map((o) => {
                const p = pipeOf(o);
                return (
                  <li key={o.id}>
                    <button className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 py-2 text-left hover:bg-gray-50" onClick={() => p && setModal({ t: 'opp', opp: o, pipeline: p })}>
                      <b className="text-[#0F2A3A]">{o.name}</b><StatusBadge status={o.status} />
                      <span className="text-gray-500">{p?.name} · {p?.stages.find((s) => s.id === o.stage_id)?.name}</span>
                      <span className="ml-auto font-semibold">{money(o.value)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* v1.10.0 · Cotizaciones y facturas */}
        <section className="rounded-xl border border-gray-200 bg-white p-4" data-testid="contact-docs">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-[14px] font-bold text-[#0F2A3A]">Cotizaciones y facturas</h3>
            <div className="flex gap-2">
              <button className={btn.ghost} onClick={() => setModal({ t: 'doc', kind: 'quote', doc: null })}><Plus size={14} /> Cotización</button>
              <button className={btn.ghost} onClick={() => setModal({ t: 'doc', kind: 'invoice', doc: null })}><Plus size={14} /> Factura</button>
            </div>
          </div>
          {docs.quotes.length + docs.invoices.length === 0 ? <p className="text-sm text-gray-500">Sin documentos.</p> : (
            <ul className="divide-y text-sm">
              {[...docs.invoices.map((d) => ({ ...d, kind: 'invoice' })), ...docs.quotes.map((d) => ({ ...d, kind: 'quote' }))].map((d) => (
                <li key={d.id}>
                  <button className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 py-2 text-left hover:bg-gray-50" onClick={() => setModal({ t: 'doc', kind: d.kind, doc: d })}>
                    <b className="text-[#0F2A3A]">{d.number}</b><span className="text-gray-500">{d.title || (d.kind === 'quote' ? 'Cotización' : 'Factura')}</span>
                    <span className="ml-auto font-semibold">{money2(d.total)}{d.kind === 'invoice' && d.balance > 0 && d.balance < d.total ? ` · saldo ${money2(d.balance)}` : ''}</span>
                    <DocBadge kind={d.kind} status={d.display_status} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* v1.9.0 · Citas */}
        <section className="rounded-xl border border-gray-200 bg-white p-4" data-testid="contact-appointments">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-[14px] font-bold text-[#0F2A3A]">Citas</h3>
            <button className={btn.ghost} disabled={calendars.length === 0} title={calendars.length ? '' : 'Primero cree un calendario'}
                    onClick={() => setModal({ t: 'appt', appt: null })} data-testid="contact-book"><Plus size={14} /> Agendar</button>
          </div>
          {appts.length === 0 ? <p className="text-sm text-gray-500">{calendars.length ? 'Sin citas.' : 'Aún no hay calendarios: créelos en la pestaña Calendario.'}</p> : (
            <ul className="divide-y text-sm">
              {[...appts].reverse().map((a) => (
                <li key={a.id}>
                  <button className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 py-2 text-left hover:bg-gray-50" onClick={() => setModal({ t: 'appt', appt: a })}>
                    <b className="text-[#0F2A3A]">{apptWhen(a)}</b>
                    <span className="text-gray-500">{a.calendar_name}{users[a.assigned_to] ? ` · ${users[a.assigned_to]}` : ''}</span>
                    <span className="ml-auto"><ApptBadge status={a.status} /></span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-xl border border-gray-200 bg-white">
          <div className="flex gap-1 border-b px-2">
            <button className={tabCls(tab === 'notes')} onClick={() => setTab('notes')}>Notas ({c.notes.length})</button>
            <button className={tabCls(tab === 'tasks')} onClick={() => setTab('tasks')}>Tareas ({c.tasks.filter((t) => t.status === 'pending').length})</button>
            <button className={tabCls(tab === 'activity')} onClick={() => setTab('activity')}>Actividad</button>
          </div>
          <div className="p-4">
            {tab === 'notes' && (
              <div className="space-y-3">
                <textarea className={inputCls} rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Escriba una nota sobre este contacto" />
                <button className={btn.primary} disabled={!note.trim()} onClick={addNote}>Agregar nota</button>
                <ul className="space-y-2">
                  {c.notes.map((n) => (
                    <li key={n.id} className="rounded-lg bg-gray-50 p-3 text-sm">
                      <div className="whitespace-pre-wrap text-gray-800">{n.body}</div>
                      <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                        <span>{n.by} · {fdatetime(n.created_at)}</span>
                        <button onClick={() => delNote(n)} className="hover:text-red-600">Eliminar</button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {tab === 'tasks' && (
              <div>
                <button className={btn.ghost} onClick={() => setModal({ t: 'task', task: null })}><Plus size={14} /> Tarea</button>
                {c.tasks.length === 0 ? <p className="mt-3 text-sm text-gray-500">Sin tareas.</p> : (
                  <ul className="mt-2 divide-y">
                    {c.tasks.map((t) => <TaskRow key={t.id} t={t} users={users} showContact={false} onToggle={toggleTask} onEdit={(x) => setModal({ t: 'task', task: x })} onDelete={delTask} />)}
                  </ul>
                )}
              </div>
            )}
            {tab === 'activity' && (
              <ol className="relative space-y-3 border-l border-gray-200 pl-4">
                {c.activities.map((a) => (
                  <li key={a.id} className="text-sm">
                    <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-[#2F3D8E]" />
                    <div className="text-gray-800"><b className="mr-1 text-xs uppercase tracking-wide text-gray-500">{ACT_LABEL[a.type] || a.type}</b>{a.text}</div>
                    <div className="text-xs text-gray-500">{a.by} · {fdatetime(a.at)}</div>
                  </li>
                ))}
                {c.activities.length === 0 && <li className="text-sm text-gray-500">Sin actividad registrada.</li>}
              </ol>
            )}
          </div>
        </section>
      </div>

      {modal?.t === 'doc' && (
        <DocModal key={modal.doc?.id || `new-${modal.kind}`} kind={modal.kind} meta={meta} doc={modal.doc} preset={{ contact: { id: c.id, name: c.name } }} onChanged={() => { loadDocs(); load(); }}
                  onClose={(next) => setModal(next?.id ? { t: 'doc', kind: next.kind, doc: next } : null)} />
      )}
      {modal?.t === 'appt' && (
        <AppointmentForm meta={meta} calendars={calendars} appt={modal.appt} preset={{ contact: { id: c.id, name: c.name, email: c.email } }}
                         onClose={() => setModal(null)} onSaved={() => { setModal(null); loadAppts(); load(); }} />
      )}
      {modal?.t === 'task' && <TaskForm meta={meta} task={modal.task} contactId={c.id} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal?.t === 'opp' && modal.pipeline && (
        <OppForm meta={meta} pipeline={modal.pipeline} opp={modal.opp} presetContact={{ id: c.id, name: c.name }}
                 onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}
